import { Box, Button, Paper } from '@mui/material';
import { MainLayout } from 'layouts/MainLayout';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import React from 'react';
import { GridColDef, GridRowParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { CONTENT_AVAILABILITY_VIEW } from 'config/routes';
import { useNavigate } from 'react-router-dom';
import { MuiTheme } from 'config/theme';
import { useLocationsData } from 'components/location/useLocationsData';
import { GridFilterInput } from 'lib/grid/GridFilterInput';
import { GridStorageName } from 'lib/MuiGrid/StateController';

export const OrderingAvailabilityPage: React.FC = () => {
    const [search, setSearch] = React.useState<string>();
    const navigate = useNavigate();
    const { items, loading, hasPreviousPage, hasNextPage, onNextPage, onPreviousPage } =
        useLocationsData(search);
    const handleSearchChange = React.useCallback((value: string) => {
        setSearch(value);
    }, []);
    const handleRowClick = React.useCallback(
        (params: GridRowParams) => {
            navigate(CONTENT_AVAILABILITY_VIEW.replace(':locationId', String(params?.id)));
        },
        [navigate]
    );
    const Pagination = React.useCallback(
        () => (
            <Box
                display="flex"
                justifyContent="flex-end"
                gap={1}
                sx={{ paddingRight: MuiTheme.spacing(6), paddingY: MuiTheme.spacing(1) }}
            >
                <Button size="small" disabled={!hasPreviousPage} onClick={onPreviousPage}>
                    Previous
                </Button>
                <Button size="small" disabled={!hasNextPage} onClick={onNextPage}>
                    Next
                </Button>
            </Box>
        ),
        [hasPreviousPage, hasNextPage, onPreviousPage, onNextPage]
    );
    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                headerName: 'Location ID',
                field: '_id',
                disableReorder: true,
                disableExport: true,
                width: 300
            },
            {
                headerName: 'Name',
                field: 'title',
                disableReorder: true,
                disableExport: true,
                width: 400
            }
        ],
        []
    );
    const getUniqueKey = React.useCallback((row: GridValidRowModel) => row._id, []);
    React.useEffect(() => {
        if (items && items.length === 1) {
            navigate(CONTENT_AVAILABILITY_VIEW.replace(':locationId', items[0]._id), { replace: true });
        }
    }, [navigate, items]);
    return (
        <MainLayout
            pageName="Ordering Availability"
            pageDescription="Manage product availability per location"
            noScroll
        >
            <Box paddingBottom={1}>
                <GridFilterInput
                    value={search}
                    onChange={handleSearchChange}
                    label="Location title"
                    name="search"
                    size="small"
                />
            </Box>
            <Paper sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                <MuiGrid
                    rows={items}
                    columns={columns}
                    hideFooter={false}
                    hideFooterRowCount
                    pagination
                    disableColumnFilter
                    paginationMode="server"
                    loading={loading}
                    slots={{
                        pagination: Pagination
                    }}
                    getRowId={getUniqueKey}
                    storageName={GridStorageName.OrderingAvailability}
                    enableRowClick
                    onRowClick={handleRowClick}
                />
            </Paper>
        </MainLayout>
    );
};
